 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useState, useEffect, useMemo } from 'react';
import { createSyncMiddleware, WithSyncControls } from './middleware/syncMiddleware';
import { useProductStore } from './productStore';
import { useCompanyStore } from './companyStore';
import { useGameStore } from './gameStore';
import { getCompanies } from '../utils/companyDataLoader';
import { ProductManager } from '../services/productManager';
import type {
  CompetitorAction,
  MarketSegment,
  EconomicCycle,
  MarketEvent,
  MarketFactors,
  TechnologyDisruption,
  RegionalMarket,
  MarketConsolidation
} from '../types/tycoon';
import type { Company } from '../types/company';
import type { Product } from '@smartphone-tycoon/shared-core';

export interface Competitor extends Company {
  strategy: {
    aggressiveness: number;
    innovationFocus: number;
    priceCompetitiveness: number;
    marketingBudget: number;
    rdBudget: number;
    preferredSegments: string[];
  };
  products: {
    id: string;
    name: string;
    price: number;
    techScore: number;
    marketShare: number;
    launchDate: number;
  }[];
  marketPerformance: {
    revenue: number;
    growth: number;
    profitability: number;
  };
}

// Helper functions for Map serialization
const mapToObject = <K extends string, V>(map: Map<K, V>): Record<string, V> => {
  const obj: Record<string, V> = {};
  map.forEach((value, key) => {
    obj[key] = value;
  });
  return obj;
};

const objectToMap = <K extends string, V>(obj: Record<string, V>): Map<K, V> => {
  const map = new Map<K, V>();
  Object.entries(obj).forEach(([key, value]) => {
    map.set(key as K, value);
  });
  return map;
};

interface MarketStore extends WithSyncControls {
  // State
  competitors: Map<string, Competitor>;
  marketSegments: Map<string, MarketSegment>;
  economicCycle: EconomicCycle;
  marketEvents: MarketEvent[];
  competitorActions: CompetitorAction[];
  marketFactors: MarketFactors;
  technologyDisruptions: TechnologyDisruption[];
  regionalMarkets: Map<string, RegionalMarket>;
  marketConsolidation: MarketConsolidation;

  // Actions
  updateCompetitors: (competitors: Competitor[]) => void;
  updateCompetitor: (competitorId: string, updates: Partial<Competitor>) => void;
  addMarketEvent: (event: MarketEvent) => void;
  removeMarketEvent: (eventId: string) => void;
  processCompetitorAction: (action: CompetitorAction) => void;
  updateEconomicCycle: (cycle: Partial<EconomicCycle>) => void;
  updateMarketFactors: (factors: Partial<MarketFactors>) => void;
  addTechnologyDisruption: (disruption: TechnologyDisruption) => void;
  updateMarketSegment: (segmentId: string, updates: Partial<MarketSegment>) => void;
  updateRegionalMarket: (regionId: string, updates: Partial<RegionalMarket>) => void;
  updateMarketConsolidation: (consolidation: Partial<MarketConsolidation>) => void;

  // Computed selectors
  selectCompetitorsBySegment: (segmentId: string) => Competitor[];
  selectActiveMarketEvents: () => MarketEvent[];
  selectMarketTrends: () => { segment: string; trend: number; growth: number }[];

  // Complex actions
  simulateMarketResponse: (playerAction: { type: string; data: unknown }) => void;
  forecastMarketChanges: (months: number) => void;
  
  // Reset
  resetMarket: () => void;

  // Hydration from save
  hydrateFromSave: (saveData: {
    totalMarketSize?: number;
    marketGrowthRate?: number;
    economicPhase?: string;
    competitorActions?: CompetitorAction[];
    technologicalDisruptions?: TechnologyDisruption[];
    marketEvents?: MarketEvent[];
    playerActions?: unknown[];
  } | undefined) => void;
}

const defaultEconomicCycle: EconomicCycle = {
  phase: 'expansion',
  duration: 24,
  remainingMonths: 24,
  gdpGrowth: 0.03,
  consumerConfidence: 0.75,
  unemploymentRate: 0.05,
  inflationRate: 0.02,
  marketMultiplier: 1.1,
};

const defaultMarketFactors: MarketFactors = {
  economic: 1.0,
  seasonal: 1.0,
  technology: 1.0,
  competition: 1.0,
  regulatory: 1.0,
  social: 1.0,
};

const defaultMarketConsolidation: MarketConsolidation = {
  trigger: 'market_maturity',
  phase: 'fragmented',
  competitorCount: 10,
  marketShareConcentration: 0.3,
  barrierToEntry: 0.5,
  acquisitionProbability: 0.1,
  newEntrantThreat: 0.3,
};

// Partialize function for selective persistence
const partializeMarketStore = (state: MarketStore) => {
  // Don't persist temporary UI selections and calculations
  const {
    selectCompetitorsBySegment: _selectCompetitorsBySegment,
    selectActiveMarketEvents: _selectActiveMarketEvents,
    selectMarketTrends: _selectMarketTrends,
    simulateMarketResponse: _simulateMarketResponse,
    forecastMarketChanges: _forecastMarketChanges,
    // Exclude action functions
    updateCompetitors: _updateCompetitors,
    updateCompetitor: _updateCompetitor,
    addMarketEvent: _addMarketEvent,
    removeMarketEvent: _removeMarketEvent,
    processCompetitorAction: _processCompetitorAction,
    updateEconomicCycle: _updateEconomicCycle,
    updateMarketFactors: _updateMarketFactors,
    addTechnologyDisruption: _addTechnologyDisruption,
    updateMarketSegment: _updateMarketSegment,
    updateRegionalMarket: _updateRegionalMarket,
    updateMarketConsolidation: _updateMarketConsolidation,
    resetMarket: _resetMarket,
    sync: _sync,
    ...persistableState
  } = state;

  // Convert Maps to objects for serialization
  return {
    competitors: mapToObject(persistableState.competitors),
    marketSegments: mapToObject(persistableState.marketSegments),
    economicCycle: persistableState.economicCycle,
    marketEvents: persistableState.marketEvents || [],
    competitorActions: (persistableState.competitorActions || []).slice(-100), // Keep last 100 actions
    marketFactors: persistableState.marketFactors,
    technologyDisruptions: persistableState.technologyDisruptions || [],
    regionalMarkets: mapToObject(persistableState.regionalMarkets),
    marketConsolidation: persistableState.marketConsolidation,
  };
};

// Sync configuration for market store
const marketSyncConfig = {
  routing: {
    rules: [
      // Complex market data goes to IndexedDB
      {
        match: (path) => 
          path.includes('competitors') || 
          path.includes('marketSegments') ||
          path.includes('regionalMarkets'),
        target: 'indexedDB' as const,
        priority: 100
      },
      // Market events use IndexedDB for time-series optimization
      {
        match: (path) => path.includes('marketEvents'),
        target: 'indexedDB' as const,
        priority: 90
      },
      // Economic data and factors stay in localStorage for quick access
      {
        match: (path) => 
          path.includes('economicCycle') || 
          path.includes('marketFactors'),
        target: 'localStorage' as const,
        priority: 80
      },
      // Default to localStorage
      {
        match: () => true,
        target: 'localStorage' as const,
        priority: 0
      }
    ]
  },
  behavior: {
    debounceDelay: 5000, // 5-second debounce for market updates
    batchSize: 20,
    enableAutoSync: true,
    syncOnReconnect: true
  },
  hooks: {
    beforeSync: (data) => {
      // If data is not a full state object (e.g., just a field being synced), return as-is
      if (!data || typeof data !== 'object' || !('competitors' in data || 'marketEvents' in data)) {
        return data;
      }

      // Ensure data integrity before sync
      if (data.competitors) {
        data.competitors = objectToMap(data.competitors);
      }
      if (data.marketSegments) {
        data.marketSegments = objectToMap(data.marketSegments);
      }
      if (data.regionalMarkets) {
        data.regionalMarkets = objectToMap(data.regionalMarkets);
      }
      return partializeMarketStore(data);
    },
    afterSync: (result) => {
      if (!result.success) {
        console.error('[MarketStore] Sync failed:', result.error);
      }
    },
    onError: (error) => {
      console.error('[MarketStore] Sync error:', error);
      // Sync health error recording is handled by syncMiddleware
    }
  },
  monitoring: {
    trackMetrics: true,
    metricsCallback: (metrics) => {
      // Metrics are handled by syncMiddleware which has access to syncHealthService
      // console.debug('[MarketStore] Sync metrics:', {
      //   lastSyncTime: metrics.lastSyncTime,
      //   syncDuration: metrics.averageSyncTime,
      //   pendingChanges: metrics.pendingItems,
      //   failureRate: metrics.failedSyncs / (metrics.totalSyncs || 1)
      // });
    }
  }
};

export const useMarketStore = create<MarketStore>()(
  devtools(
    createSyncMiddleware(
      'marketStore',
      marketSyncConfig
    )(
      (set, get) => ({
      // Initial state
      competitors: new Map(),
      marketSegments: new Map(),
      economicCycle: defaultEconomicCycle,
      marketEvents: [],
      competitorActions: [],
      marketFactors: defaultMarketFactors,
      technologyDisruptions: [],
      regionalMarkets: new Map(),
      marketConsolidation: defaultMarketConsolidation,

      // Actions
      updateCompetitors: (competitors) =>
        set(
          () => {
            const competitorMap = new Map<string, Competitor>();
            competitors.forEach(comp => competitorMap.set(comp.id, comp));
            return { competitors: competitorMap };
          },
          false,
          'updateCompetitors'
        ),

      updateCompetitor: (competitorId, updates) =>
        set(
          (state) => {
            const competitors = new Map(state.competitors);
            const existing = competitors.get(competitorId);
            if (existing) {
              competitors.set(competitorId, { ...existing, ...updates });
            }
            return { competitors };
          },
          false,
          'updateCompetitor'
        ),

      addMarketEvent: (event) =>
        set(
          (state) => ({
            marketEvents: [...state.marketEvents, event],
          }),
          false,
          'addMarketEvent'
        ),

      removeMarketEvent: (eventId) =>
        set(
          (state) => ({
            marketEvents: state.marketEvents.filter(e => e.id !== eventId),
          }),
          false,
          'removeMarketEvent'
        ),

      processCompetitorAction: (action) =>
        set(
          (state) => {
            const competitorActions = [...state.competitorActions, action];
            
            // Limit history to last 100 actions
            if (competitorActions.length > 100) {
              competitorActions.shift();
            }
            
            return { competitorActions };
          },
          false,
          'processCompetitorAction'
        ),

      updateEconomicCycle: (cycle) =>
        set(
          (state) => ({
            economicCycle: { ...state.economicCycle, ...cycle },
          }),
          false,
          'updateEconomicCycle'
        ),

      updateMarketFactors: (factors) =>
        set(
          (state) => ({
            marketFactors: { ...state.marketFactors, ...factors },
          }),
          false,
          'updateMarketFactors'
        ),

      addTechnologyDisruption: (disruption) =>
        set(
          (state) => ({
            technologyDisruptions: [...state.technologyDisruptions, disruption],
          }),
          false,
          'addTechnologyDisruption'
        ),

      updateMarketSegment: (segmentId, updates) =>
        set(
          (state) => {
            const marketSegments = new Map(state.marketSegments);
            const existing = marketSegments.get(segmentId);
            if (existing) {
              marketSegments.set(segmentId, { ...existing, ...updates });
            }
            return { marketSegments };
          },
          false,
          'updateMarketSegment'
        ),

      updateRegionalMarket: (regionId, updates) =>
        set(
          (state) => {
            const regionalMarkets = new Map(state.regionalMarkets);
            const existing = regionalMarkets.get(regionId);
            if (existing) {
              regionalMarkets.set(regionId, { ...existing, ...updates });
            }
            return { regionalMarkets };
          },
          false,
          'updateRegionalMarket'
        ),

      updateMarketConsolidation: (consolidation) =>
        set(
          (state) => ({
            marketConsolidation: { ...state.marketConsolidation, ...consolidation },
          }),
          false,
          'updateMarketConsolidation'
        ),

      // Computed selectors
      selectCompetitorsBySegment: (segmentId) => {
        const state = get();
        const competitors = Array.from(state.competitors.values());
        return competitors.filter(comp => 
          comp.strategy.preferredSegments.includes(segmentId)
        );
      },

      selectActiveMarketEvents: () => {
        const state = get();
        return state.marketEvents.filter(event => {
          // Filter for active events based on duration and other criteria
          return event.effects.duration > 0;
        });
      },

      selectMarketTrends: () => {
        const state = get();
        const segments = Array.from(state.marketSegments.values());
        return segments.map(segment => ({
          segment: segment.name,
          trend: segment.growthRate,
          growth: segment.size * segment.growthRate,
        }));
      },

      // Complex actions
      simulateMarketResponse: (playerAction) =>
        set(
          (state) => {
            // This would integrate with CompetitorAIService
            // For now, just log the action
            console.log('Market response to player action:', playerAction);
            return state;
          },
          false,
          'simulateMarketResponse'
        ),

      forecastMarketChanges: (months) =>
        set(
          (state) => {
            // Simple forecast based on current trends
            const forecast = {
              economicOutlook: state.economicCycle.phase,
              expectedGrowth: state.marketFactors.economic * months * 0.02,
              riskFactors: state.technologyDisruptions.length,
            };
            
            console.log(`Market forecast for ${months} months:`, forecast);
            return state;
          },
          false,
          'forecastMarketChanges'
        ),

      // Reset
      resetMarket: () =>
        set(
          {
            competitors: new Map(),
            marketSegments: new Map(),
            economicCycle: defaultEconomicCycle,
            marketEvents: [],
            competitorActions: [],
            marketFactors: defaultMarketFactors,
            technologyDisruptions: [],
            regionalMarkets: new Map(),
            marketConsolidation: defaultMarketConsolidation,
          },
          false,
          'resetMarket'
        ),

      // Hydration from save
      hydrateFromSave: (saveData) =>
        set(
          (state) => {
            if (!saveData) return state;

            return {
              competitors: state.competitors, // Loaded separately via competitor system
              marketSegments: state.marketSegments,
              economicCycle: {
                ...defaultEconomicCycle,
                phase: (saveData.economicPhase as EconomicCycle['phase']) ?? 'expansion',
                marketMultiplier: 1 + (saveData.marketGrowthRate ?? 0.08),
              },
              marketEvents: saveData.marketEvents ?? [],
              competitorActions: saveData.competitorActions ?? [],
              marketFactors: {
                ...defaultMarketFactors,
                economic: saveData.totalMarketSize
                  ? saveData.totalMarketSize / 500000000
                  : 1.0,
              },
              technologyDisruptions: saveData.technologicalDisruptions ?? [],
              regionalMarkets: state.regionalMarkets,
              marketConsolidation: defaultMarketConsolidation,
            };
          },
          false,
          'hydrateFromSave'
        ),
      })
    ),
    {
      name: 'market-store',
    }
  )
);

// Selectors
export const selectCompetitors = (state: MarketStore) => state.competitors;
export const selectMarketSegments = (state: MarketStore) => state.marketSegments;
export const selectEconomicCycle = (state: MarketStore) => state.economicCycle;
export const selectMarketEvents = (state: MarketStore) => state.marketEvents;
export const selectCompetitorActions = (state: MarketStore) => state.competitorActions;
export const selectMarketFactors = (state: MarketStore) => state.marketFactors;

// Computed selector for all products (player + competitors)
export const useAllProducts = () => {
  const playerProducts = useProductStore(state => state.products);
  const companyName = useCompanyStore(state => state.company?.name);
  const competitors = useMarketStore(state => state.competitors);
  const gameDate = useGameStore(state => state.gameDate);
  const currentYear = gameDate.year;
  const [competitorProducts, setCompetitorProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Trigger a delayed retry on mount to catch competitor products
  // that may have been seeded after initial database load
  useEffect(() => {
    const timer = setTimeout(() => {
      setRefreshKey(prev => prev + 1);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Load competitor products from database - refresh on every game day change
  useEffect(() => {
    const loadCompetitorProducts = async () => {
      try {
        const result = await ProductManager.getAllProducts();

        if (result.success && result.data) {
          // Filter for competitor products (those with companyId !== 'player')
          const competitors = result.data.filter(product =>
            product.metadata?.companyId &&
            product.metadata.companyId !== 'player' &&
            product.status === 'released'
          );

          // Diagnostic logging (remove after validation)
          if (competitors.length > 0) {
            // console.log(`🔍 [useAllProducts] Loaded ${competitors.length} competitor products from database`);
          }

          setCompetitorProducts(competitors);
        }
      } catch (error) {
        console.error('❌ [useAllProducts] Failed to load competitor products:', error);
        setCompetitorProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadCompetitorProducts();
  }, [gameDate.year, gameDate.month, gameDate.day, refreshKey]); // Re-load on every game day change and after delayed retry

  // Get company names from static data
  const companies = getCompanies({ year: currentYear });
  const getCompanyName = (companyId: string) => {
    if (!companies) return companyId;
    const company = companies.find(c => c.id === companyId);
    return company?.name || companyId;
  };

  // Process all products
  const allProducts = useMemo(() => {
    const playerProductsList = playerProducts.map(p => ({
      id: p.id,
      companyName: companyName || 'Your Company',
      name: p.name,
      price: p.price,
      techScore: p.techScore,
      userRating: p.salesMetrics?.averageRating || 0,
      marketAppeal: p.marketAppeal,
      releaseDate: p.metadata?.releaseDate,
      discontinuedDate: p.metadata?.discontinuedDate,
      status: p.status,
      unitsSold: p.salesMetrics?.unitsSold || 0,
      marketShare: p.salesMetrics?.marketShare || 0,
      isPlayerProduct: true
    }));

    // Convert competitor products from database to display format
    const competitorProductsList = competitorProducts.map(p => ({
      id: p.id,
      companyName: getCompanyName(p.metadata?.companyId || 'unknown'),
      name: p.name,
      price: p.price,
      techScore: p.techScore,
      userRating: p.salesMetrics?.averageRating || 0,
      marketAppeal: p.marketAppeal || 0,
      releaseDate: p.metadata?.releaseDate,
      discontinuedDate: p.metadata?.discontinuedDate,
      status: p.status,
      unitsSold: p.salesMetrics?.unitsSold || 0,
      marketShare: p.salesMetrics?.marketShare || 0,
      isPlayerProduct: false
    }));

    const combined = [...playerProductsList, ...competitorProductsList];

    // Removed verbose logging - hook now refreshes on every game tick

    return combined;
  // TODO: Fix react-hooks/exhaustive-deps - Missing dependency: getCompanyName
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerProducts, competitorProducts, companyName]);

  return {
    allProducts,
    competitorProducts: allProducts.filter(p => !p.isPlayerProduct),
    loading,
    error: null,
    refetch: () => {} // No-op since data is reactive
  };
};